/* eslint-disable react-refresh/only-export-components */
import {createContext, useCallback, useContext, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import { apiClient } from '../services/apiClient'
import toast from 'react-hot-toast'
import {getHighestRole, hasRoleAccess} from '../components/navigation/utils/roleHierarchy'
import type {User, UserContextValue, UserRole, ProfileUpdateResult, RoleHelpers} from '../types/auth'
import React from 'react'

const UserContext = createContext<UserContextValue | null>(null)

/**
 * UserContext manages user data and user-related operations
 * Separated from AuthContext for better separation of concerns
 * Enhanced with role-based access control helpers
 */
export const UserProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const {t} = useTranslation('common')
  const [user, setUser] = useState<User | null>(null)

  const updateUser = useCallback((updatedUser: User) => {
    setUser(updatedUser)
    // Strip roles before persisting — roles must only come from server
    const { roles, ...displayData } = updatedUser  // eslint-disable-line @typescript-eslint/no-unused-vars
    localStorage.setItem('user', JSON.stringify(displayData))
  }, [])

  const refreshUser = useCallback(async (): Promise<User> => {
    try {
      const response = await apiClient.get('/users/me')
      const userData = response.data as User
      setUser(userData)
      const { roles, ...displayData } = userData  // eslint-disable-line @typescript-eslint/no-unused-vars
      localStorage.setItem('user', JSON.stringify(displayData))
      return userData
    } catch (error) {
      console.error('Error refreshing user data:', error)
      throw error
    }
  }, [])

  const updateProfile = useCallback(async (profileData: Record<string, unknown>): Promise<ProfileUpdateResult> => {
    try {
      const response = await apiClient.put('/users/me', profileData)
      const updatedUser = response.data as User
      updateUser(updatedUser)
      toast.success(t('status.changes_saved'))
      return { success: true, user: updatedUser }
    } catch (error) {
      const err = error as {response?: {data?: {message?: string}}}
      const message = err.response?.data?.message || 'Profile update failed'
      return { success: false, error: message }
    }
  }, [updateUser, t])

  const clearUser = useCallback(() => {
    setUser(null)
    localStorage.removeItem('user')
  }, [])

  const loadUserFromStorage = useCallback((): User | null => {
    const storedUser = localStorage.getItem('user')
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser) as User
        // Never trust roles from localStorage — force empty until server confirms
        userData.roles = []
        setUser(userData)
        return userData
      } catch (error) {
        console.error('Error parsing stored user data:', error)
        localStorage.removeItem('user')
      }
    }
    return null
  }, [])

  // Computed role-based properties
  const roleHelpers: RoleHelpers = useMemo(() => {
    if (!user) return {
      isManager: false,
      isSuperuser: false,
      hasAdminAccess: false,
      primaryRole: null
    }

    const userRoles = user.roles || []
    const primaryRole = getHighestRole(userRoles) as UserRole | null

    return {
      isManager: userRoles.includes('MANAGER'),
      isSuperuser: userRoles.includes('SUPERUSER'),
      hasAdminAccess: userRoles.includes('MANAGER') || userRoles.includes('SUPERUSER'),
      primaryRole
    }
  }, [user])

  // Role checking functions
  const hasRole = useCallback((role: UserRole): boolean => {
    return user?.roles?.includes(role) || false
  }, [user])

  const canAccessRoute = useCallback((requiredRole: UserRole): boolean => {
    if (!user || !requiredRole) return false
    return hasRoleAccess(user.roles || [], requiredRole)
  }, [user])

  const value: UserContextValue = {
    user,
    updateUser,
    refreshUser,
    updateProfile,
    clearUser,
    loadUserFromStorage,
    // Role-based helpers
    ...roleHelpers,
    hasRole,
    canAccessRoute
  }

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  )
}

export const useUser = (): UserContextValue => {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}
